export interface LandRegisterAttribution {
    mapproxy_layer: string;
    api_url: string;
    license_label: string;
    license_url: string;
    service_label: string;
    service_license?: string;
}

/**
 * Neutrale Attribution für unbekanntes Bundesland (z. B. fehlender state_code, #19302 Q-B6):
 * keine Lizenz- und keine Dienstangaben, damit keine falsche Lizenzaussage entsteht.
 * Erkennung über {@link isDefaultLandRegisterAttribution}.
 */
export const DEFAULT_LAND_REGISTER_ATTRIBUTION: LandRegisterAttribution = {
    mapproxy_layer: "",
    api_url: "",
    license_label: "",
    license_url: "",
    service_label: "zuständiges Landesamt",
};

export function isDefaultLandRegisterAttribution(attribution: LandRegisterAttribution): boolean {
    return attribution === DEFAULT_LAND_REGISTER_ATTRIBUTION;
}

/** Land register (Liegenschaftskataster) WMS/license attribution per German state code ("01"-"16"). */
export function getLandRegisterAttributionsByStateCode(): Record<string, LandRegisterAttribution> {
    const currentYear = new Date().getFullYear();
    return {
        "01": {
            // Schleswig-Holstein — https://danord.gdi-sh.de/viewer/resources/apps/ALKIS_FS/index.html?lang=de#/
            mapproxy_layer: "schleswig_holstein_wms",
            api_url: "https://service.gdi-sh.de/wss/service/ALKIS-WMS/httpauth",
            license_label: "Creative Commons Namensnennung 4.0 International (CC BY 4.0)",
            license_url: "https://creativecommons.org/licenses/by/4.0/",
            service_label: "Landesamt für Vermessung und Geoinformation Schleswig-Holstein",
            service_license: `© GeoBasis-DE/LVermGeo SH/CC-BY 4.0`,
        },
        "02": {
            // Hamburg
            mapproxy_layer: "hamburg_wms",
            api_url: "https://geodienste.hamburg.de/HH_WMS_ALKIS_Basiskarte",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: `Freie und Hansestadt Hamburg, Landesbetrieb Geoinformation und Vermessung`,
            service_license: `© FHH, LGV [${currentYear}], dl-de/by-2-0`,
        },
        "03": {
            // Niedersachsen – Lizenz laut AccessConstraints der WMS-GetCapabilities: CC BY 4.0 (#19302 L-B13)
            mapproxy_layer: "niedersachsen_wms",
            api_url: "https://opendata.lgln.niedersachsen.de/doorman/noauth/alkis_wms",
            license_label: "Creative Commons Namensnennung 4.0 International (CC BY 4.0)",
            license_url: "https://creativecommons.org/licenses/by/4.0/",
            service_label: "Landesamt für Geoinformation und Landesvermessung Niedersachsen",
            service_license: `© GeoBasis-DE/LGLN [${currentYear}], CC BY 4.0`,
        },
        "04": {
            // Bremen
            mapproxy_layer: "bremen_wms",
            api_url: "https://geoportal.bremen.de/gdi3",
            license_label: "Creative Commons Namensnennung 4.0 International (CC BY 4.0)",
            license_url: "https://creativecommons.org/licenses/by/4.0/deed.de",
            service_label: "Landesamt GeoInformation Bremen",
            service_license: `© Landesamt GeoInformation Bremen/CC-BY 4.0`,
        },
        "05": {
            // Nordrhein-Westfalen (NRW)
            mapproxy_layer: "nordrhein_westfalen_wms",
            api_url: "https://www.wms.nrw.de/geobasis/wms_nw_alkis",
            license_label: "Datenlizenz Deutschland – Zero – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/zero-2-0",
            service_label: "Geoinformationszentrum NRW",
        },
        "06": {
            // Hessen
            mapproxy_layer: "hessen_wms",
            api_url: "https://www.gds-srv.hessen.de/cgi-bin/lika-services/ogc-free-maps.ows",
            license_label: "Datenlizenz Deutschland – Zero – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/zero-2-0",
            service_label: "Hessischen Landesamt für Bodenmanagement und Geoinformation",
        },
        "07": {
            // Rheinland-Pfalz
            mapproxy_layer: "rheinland_pfalz_wms",
            api_url: "https://geo5.service24.rlp.de/wms/liegenschaften_rp.fcgi",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Landesamt für Vermessung und Geobasisinformation (LVermGeo) Rheinland-Pfalz",
            service_license: `©GeoBasis-DE / LVermGeoRP [${currentYear}], http://www.lvermgeo.rlp.de, dl-de/by-2-0`,
        },
        "08": {
            // Baden-Württemberg
            mapproxy_layer: "baden_wurttemberg_wms",
            api_url: "https://owsproxy.lgl-bw.de/owsproxy/ows/WMS_LGL-BW_ALKIS_Basis_Vertrieb",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Landesamt für Geoinformation und Landentwicklung Baden-Württemberg",
            service_license: "Geobasisdaten © LGL, www.lgl-bw.de, dl-de/by-2-0",
        },
        "09": {
            // Bayern
            mapproxy_layer: "bayern_wms",
            api_url: "https://geoservices.bayern.de/pro/wms/alkis/v1/flurkarte",
            license_label: "Nutzungsbedingungen Geodaten Online Bayern",
            license_url: "https://geodatenonline.bayern.de/geodatenonline/seiten/nutzungsbedingungen",
            service_label: "Landesamt für Digitalisierung, Breitband und Vermessung",
            service_license: "https://geodatenonline.bayern.de/geodatenonline/seiten/nutzungsbedingungen",
        },
        "10": {
            // Saarland
            mapproxy_layer: "saarland_cadastral_wms",
            api_url: "https://geoportal.saarland.de/gdi-sl/inspirewms_Flurstuecke_Grundstuecke_ALKIS",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "LVGL Saarland",
            service_license: `©LVGL 2022, dl-de/by-2-0, www.lvgl.saarland.de`,
        },
        "11": {
            // Berlin
            mapproxy_layer: "berlin_wms",
            api_url: "https://fbinter.stadt-berlin.de/fb/wms/senstadt/wmsk_alkis",
            license_label: "Datenlizenz Deutschland – Zero – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/zero-2-0",
            service_label: "Senatsverwaltung für Stadtentwicklung, Bauen und Wohnen Berlin",
        },
        "12": {
            // Brandenburg
            mapproxy_layer: "brandenburg_wms",
            api_url: "https://isk.geobasis-bb.de/ows/alkis_wms",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Landesvermessung und Geobasisinformation Brandenburg (LGB)",
            service_license: `© GeoBasis-DE/LGB, dl-de/by-2-0`,
        },
        "13": {
            // Mecklenburg-Vorpommern
            mapproxy_layer: "mecklenburg_vorpommern_wms",
            api_url: "https://www.geodaten-mv.de/dienste/alkis_wms",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Landesamt für innere Verwaltung M-V, Amt für Geoinformation, Vermessung und Katasterwesen",
            service_license: `© GeoBasis-DE/M-V [${currentYear}], dl-de/by-2-0`,
        },
        "14": {
            // Sachsen
            mapproxy_layer: "sachsen_wms",
            api_url: "https://geodienste.sachsen.de/wms_geosn_alkis-adv/guest",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Landesamt für Geobasisinformation Sachsen (GeoSN) ",
            service_license: `© Geodaten Sachsen [${currentYear}], dl-de/by-2-0`,
        },
        "15": {
            // Sachsen-Anhalt
            mapproxy_layer: "sachsen_anhalt_wms",
            api_url:
                "https://www.geodatenportal.sachsen-anhalt.de/wss/service/ST_LVermGeo_ALKIS_WMS_AdV_konform_App/guest",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Landesamt für Vermessung und Geoinformation Sachsen-Anhalt",
            service_license: `© GeoBasis-DE / LVermGeo ST, dl-de/by-2-0`,
        },
        "16": {
            // Thüringen
            mapproxy_layer: "thuringen_wms",
            api_url: "https://www.geoproxy.geoportal-th.de/geoproxy/services/adv_alkis_wms_th",
            license_label: "Datenlizenz Deutschland – Namensnennung – Version 2.0",
            license_url: "https://www.govdata.de/dl-de/by-2-0",
            service_label: "Thüringer Ministerium für Infrastruktur und Landwirtschaft",
            service_license: `© GDI-Th, dl-de/by-2-0`,
        },
    };
}

export function getLandRegisterAttribution(stateCode: string | number | undefined): LandRegisterAttribution {
    const normalizedStateCode = stateCode != null ? String(stateCode).padStart(2, "0") : undefined;
    const byStateCode = getLandRegisterAttributionsByStateCode();
    return (normalizedStateCode && byStateCode[normalizedStateCode]) || DEFAULT_LAND_REGISTER_ATTRIBUTION;
}

/** Default cadastre scale denominator per Bundesland (state code -> scale); states not listed fall back to DEFAULT_CADASTRE_SCALE. */
export const DEFAULT_CADASTRE_SCALE = 1000;
export const STATE_CADASTRE_SCALE_BY_STATE_CODE: Record<string, number> = {
    "03": 750, // Niedersachsen
    "04": 750, // Bremen
    "05": 750, // NRW
    "06": 750, // Hessen
    "11": 750, // Berlin
};

export function getDefaultCadastreScale(stateCode?: string | number): number {
    if (stateCode == null) return DEFAULT_CADASTRE_SCALE;
    const normalized = String(stateCode).padStart(2, "0");
    return STATE_CADASTRE_SCALE_BY_STATE_CODE[normalized] ?? DEFAULT_CADASTRE_SCALE;
}
